import { useCallback, useEffect, useState } from 'react';
import type { FilingPlan, FilingPlanPreview } from '../shared/desktop-api';
import type { PaigeFilingPlanChannel, PaigeJobFilingPlan } from './paige.global';
import '../renderer/desktop.global';

/** The job's naming as the File naming card shows it. */
export interface JobNaming {
  /** What Paige has saved, with the job's types; null while it is read. */
  saved: PaigeJobFilingPlan | null;
  /** The naming in use: the saved one, or the suggested naming while none is saved. */
  plan: FilingPlan | null;
  /** One real example per type under `plan`, from the job's recent documents. */
  preview: FilingPlanPreview | null;
  /** Why the naming or its examples could not be read. */
  error: string | null;
  /** Saves a naming at once; rejects with Paige's reason, which the window shows. */
  save: (plan: FilingPlan) => Promise<void>;
}

const reasonOf = (failure: unknown, fallback: string) =>
  failure instanceof Error ? failure.message : fallback;

/** The suggested naming (the engine's starter) when nothing is saved yet. */
async function planInUse(read: PaigeJobFilingPlan, jobId: string): Promise<FilingPlan | null> {
  if (read.plan) return read.plan;
  const api = window.capturePointDesktop;
  return api ? api.getStarterFilingPlan(jobId) : null;
}

/**
 * The job's file naming for Job setup, Export: read through Paige (GET /v1/jobs/{id}/filing-plan),
 * the suggested naming standing in until one is saved, examples from the engine, and a save that
 * is a PUT at once (Paige saves setup cards as they change, as the retention card does).
 */
export function useJobNaming(
  channel: PaigeFilingPlanChannel | undefined,
  jobId: string | undefined,
): JobNaming {
  const [saved, setSaved] = useState<PaigeJobFilingPlan | null>(null);
  const [plan, setPlan] = useState<FilingPlan | null>(null);
  const [preview, setPreview] = useState<FilingPlanPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!channel || !jobId) return;
    let live = true;
    channel
      .read()
      .then(async (read) => {
        const inUse = await planInUse(read, jobId);
        if (!live) return;
        setSaved(read);
        setPlan(inUse);
      })
      .catch(
        (failure: unknown) =>
          live && setError(reasonOf(failure, 'Paige could not read the naming')),
      );
    return () => {
      live = false;
    };
  }, [channel, jobId]);
  useExamples(jobId, plan, setPreview);
  const save = useCallback(
    async (next: FilingPlan) => {
      if (!channel) throw new Error('This job’s naming is saved on its own page');
      const answer = await channel.save(next);
      setSaved(answer);
      setPlan(answer.plan ?? next);
      setError(null);
    },
    [channel],
  );
  return { saved, plan, preview, error, save };
}

/** One example per type for the naming in use; no examples when the engine cannot build them. */
function useExamples(
  jobId: string | undefined,
  plan: FilingPlan | null,
  setPreview: (preview: FilingPlanPreview | null) => void,
) {
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api || !jobId || !plan) return;
    let live = true;
    api
      .previewFilingPlan(jobId, plan)
      .then((value) => live && setPreview(value))
      .catch(() => live && setPreview(null));
    return () => {
      live = false;
    };
  }, [jobId, plan, setPreview]);
}
